import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { FlowPerStrikePoint, FlowMetricKey } from '../types/StockData';

interface FlowPerStrikeChartProps {
  data: FlowPerStrikePoint[];
  selectedMetric: FlowMetricKey;
  currentPrice: number;
}

const FlowPerStrikeChart: React.FC<FlowPerStrikeChartProps> = ({ data, selectedMetric, currentPrice }) => {
  // Format data for the chart and sort by strike price
  const chartData = data
    .map(point => {
      const strike = parseFloat(point.strike);
      let callValue = 0;
      let putValue = 0;
      let callLabel = '';
      let putLabel = '';

      switch (selectedMetric) {
        case 'volume':
          callValue = point.call_volume;
          putValue = point.put_volume;
          callLabel = 'Call Volume';
          putLabel = 'Put Volume';
          break;
        case 'premium':
          callValue = point.call_premium;
          putValue = point.put_premium;
          callLabel = 'Call Premium';
          putLabel = 'Put Premium';
          break;
        case 'trades':
          callValue = point.call_trades;
          putValue = point.put_trades;
          callLabel = 'Call Trades';
          putLabel = 'Put Trades';
          break;
        case 'bidAskVolume':
          callValue = point.call_volume_bid_side;
          putValue = point.call_volume_ask_side;
          callLabel = 'Bid Volume';
          putLabel = 'Ask Volume';
          break;
        case 'bidAskPremium':
          callValue = point.call_premium_bid_side;
          putValue = point.call_premium_ask_side;
          callLabel = 'Bid Premium';
          putLabel = 'Ask Premium';
          break;
        case 'volumeBias':
          callValue = point.netCallVolumeBias;
          putValue = point.netPutVolumeBias;
          callLabel = 'Call Volume Bias';
          putLabel = 'Put Volume Bias';
          break;
        case 'premiumBias':
          callValue = point.netCallPremiumBias;
          putValue = point.netPutPremiumBias;
          callLabel = 'Call Premium Bias';
          putLabel = 'Put Premium Bias';
          break;
      }

      return {
        strike,
        callValue,
        putValue,
        callLabel,
        putLabel,
        isAtm: Math.abs(strike - currentPrice) < 2.5,
      };
    })
    .sort((a, b) => a.strike - b.strike);

  // Custom tooltip formatter
  const formatTooltip = (value: number, name: string) => {
    if (selectedMetric === 'premium' || selectedMetric === 'bidAskPremium' || selectedMetric === 'premiumBias') {
      return [`$${value.toLocaleString()}`, name];
    }
    return [value.toLocaleString(), name];
  };

  // Format Y-axis values
  const formatYAxis = (value: number) => {
    if (selectedMetric === 'premium' || selectedMetric === 'bidAskPremium' || selectedMetric === 'premiumBias') {
      if (Math.abs(value) >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
      if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
      if (Math.abs(value) >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
      return `$${value.toFixed(0)}`;
    } else {
      if (Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
      if (Math.abs(value) >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
      return value.toFixed(0);
    }
  };

  const getMetricDisplayName = (metric: FlowMetricKey) => {
    const names = {
      volume: 'Volume Flow',
      premium: 'Premium Flow',
      trades: 'Trade Count',
      bidAskVolume: 'Bid/Ask Volume Flow',
      bidAskPremium: 'Bid/Ask Premium Flow',
      volumeBias: 'Volume Bias',
      premiumBias: 'Premium Bias',
    };
    return names[metric];
  };

  // Calculate Y-axis domain for better scaling
  const allValues = chartData.flatMap(d => [d.callValue, d.putValue]);
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const range = maxValue - minValue;
  const padding = range * 0.1;
  
  let yAxisDomain;
  if (minValue >= 0) {
    yAxisDomain = [0, maxValue + padding];
  } else if (maxValue <= 0) {
    yAxisDomain = [minValue - padding, 0];
  } else {
    yAxisDomain = [minValue - padding, maxValue + padding];
  }

  return (
    <div className="w-full h-[700px] bg-white rounded-lg shadow-lg p-6 mb-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          {getMetricDisplayName(selectedMetric)} by Strike
        </h3>
        <p className="text-sm text-gray-600">
          Current Price: ${currentPrice.toFixed(2)}
        </p>
      </div>
      
      <ResponsiveContainer width="100%" height="88%">
        <BarChart
          data={chartData}
          margin={{
            top: 30,
            right: 40,
            left: 60,
            bottom: 60,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis 
            dataKey="strike" 
            stroke="#6b7280"
            fontSize={12}
            tickFormatter={(value) => `$${value}`}
          />
          <YAxis 
            stroke="#6b7280"
            fontSize={12}
            domain={yAxisDomain}
            tickFormatter={formatYAxis}
          />
          <Tooltip 
            formatter={formatTooltip}
            labelFormatter={(label) => `Strike: $${label}`}
            contentStyle={{
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
            }}
          />
          <Bar 
            dataKey="callValue" 
            name={chartData[0]?.callLabel || 'Calls'}
            fill="#10b981"
            radius={[2, 2, 0, 0]}
          />
          <Bar 
            dataKey="putValue" 
            name={chartData[0]?.putLabel || 'Puts'}
            fill="#ef4444"
            radius={[2, 2, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default FlowPerStrikeChart;
